import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { Switch, Table } from 'tdesign-react';
import { connect } from 'react-redux';
import { isEmpty } from 'lodash';
import { useMemoizedFn } from 'ahooks';
import { ChevronRightCircleIcon } from 'tdesign-icons-react';
import useTable from '@/hooks/useTable';
import {
  scopeTypeOptions,
  showTypeOptions,
  variableLevelOptions,
  variableOptionalOptions,
} from '@/utils/constant';
import { isVoid, renderOptions } from '@/utils/common';
import { fetchClusterVars } from '@/slices/execVariableSlice';
import Descriptions from '@/components/Descriptions';

function VariableTable({ fetchClusterVars, cluster, variables, currentGroup }) {
  const [different, setDifferent] = useState(0); // 仅展示不同
  const groupId = currentGroup?.groupId; // 组id

  const columns = [
    {
      title: '变量key',
      colKey: 'key',
      fixed: 'left',
      width: 200,
    },
    {
      title: '展示名称',
      colKey: 'keyName',
      width: 160,
    },
    {
      title: '默认值',
      colKey: 'defaultValue',
      width: 160,
    },
    {
      title: '当前值',
      colKey: 'currentValue',
      cell: ({ row }) => (
        <Info $strength={row.currentValue !== row.defaultValue}>{row.currentValue}</Info>
      ),
      width: 160,
    },
    {
      title: '变量范围',
      colKey: 'variableLevel',
      options: variableLevelOptions,
      cell: renderOptions,
      width: 140,
    },
    {
      title: '变量域',
      colKey: 'scopeType',
      options: scopeTypeOptions,
      cell: renderOptions,
      width: 130,
    },

    {
      title: '变量类型',
      colKey: 'variableType',
      options: variableOptionalOptions,
      cell: renderOptions,
      width: 140,
    },
    {
      title: '变量组',
      colKey: 'groupName',
      width: 130,
    },
    {
      title: '展现形式',
      colKey: 'showType',
      options: showTypeOptions,
      cell: renderOptions,
      width: 140,
    },

    {
      title: '变量依赖',
      colKey: 'dependKey',
      cell: ({ row }) => (row.dependKey ? `${row.dependKey} = ${row.dependValue}` : '-'),
      width: 220,
    },
    {
      title: '脚本/组件模板引用数',
      colKey: 'stencilCount',
      width: 180,
    },
    {
      title: '备注说明',
      colKey: 'desc',
      ellipsis: true,
      width: 200,
    },
  ];

  const loadVarList = useMemoizedFn(async () => {
    if (isEmpty(cluster)) {
      return;
    }
    return fetchClusterVars({ different, clusterId: cluster.id });
  });

  const expandedRow = ({ row }) =>
    row.hostKeyInfos?.map((item) => (
      <Descriptions key={item.hostId} style={{ width: 650, paddingLeft: 48 }}>
        <Descriptions.Item label='IP' value={item.ip} />
        <Descriptions.Item label='默认值' value={row.defaultValue} />
        <Descriptions.Item
          label='当前值'
          value={
            <Info $strength={item.currentValue !== row.defaultValue}>{item.currentValue}</Info>
          }
        />
      </Descriptions>
    ));

  const { config, header, setData } = useTable({
    columns,
    onRefresh: loadVarList,
    append: (
      <Append>
        与默认值不同
        <Switch
          value={different}
          customValue={[1, 0]}
          onChange={setDifferent}
          style={{ margin: '0 10px 0 8px' }}
        />
      </Append>
    ),
  });

  useEffect(() => {
    loadVarList();
  }, [loadVarList, cluster, different]);

  useEffect(() => {
    // 没选中组
    if (isVoid(groupId)) {
      setData([]);
    }
    setData(variables.filter((item) => item.groupId === groupId));
  }, [variables, setData, groupId]);

  return (
    <Root>
      {header}
      <Table
        {...config}
        rowKey='_key'
        expandedRow={expandedRow}
        expandIcon={({ row }) => (row.variableLevel === 1 ? <ChevronRightCircleIcon /> : false)}
      />
    </Root>
  );
}

export default connect(
  (state) => ({
    cluster: state.execVariable.cluster,
    currentGroup: state.execVariable.currentGroup,
    variables: state.execVariable.variables,
  }),
  {
    fetchClusterVars,
  },
)(VariableTable);

const Root = styled.div`
  flex: 1;
  padding: 24px;
  overflow-y: auto;
  .t-table__expand-box:empty {
    display: none;
  }
`;

const Append = styled.div`
  color: #202b40;
  font-size: 12px;
  font-style: normal;
  display: flex;
  align-items: center;
  height: 32px;
`;

const Info = styled.span`
  color: ${(props) => (props.$strength ? 'var(--td-brand-color)' : 'var(--td-font-gray-1)')};
  font-weight: ${(props) => (props.$strength ? 'bolder' : 'normal')};
`;
